"use client";

import React, { useState } from 'react';
import { 
  Modal, 
  ModalContent, 
  ModalBody, 
  ModalHeader,
  ModalFooter,
  Button, 
  Image as HeroUIImage,
  useDisclosure
} from '@heroui/react';
import { X, Trash2, ZoomIn } from 'lucide-react';
import { convertImageUrl } from '@/app/lib/imageUrlConverter';

interface ImageProps {
  src: string;
  alt?: string;
  showDeleteButton?: boolean;
  removeWrapper?: boolean;
  onDeleteSuccess?: (url: string) => void;
  onDeleteError?: (error: Error) => void;
  className?: string;
}

const Image: React.FC<ImageProps> = ({
  src,
  alt = 'Image',
  showDeleteButton = false,
  removeWrapper = false,
  onDeleteSuccess,
  onDeleteError,
  className = '',
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();

  // Automatycznie konwertuj URL na dynamiczny endpoint
  const convertedSrc = convertImageUrl(src);

  const handleDeleteClick = () => {
    onConfirmOpen();
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    
    try {
      const response = await fetch("/api/images", {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: src }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      onDeleteSuccess?.(src);
      onConfirmClose();
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error('Unknown error occurred');
      onDeleteError?.(errorObj);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>      <div className={`relative group ${className}`}>
        <HeroUIImage
          removeWrapper={removeWrapper}
          src={convertedSrc}
          alt={alt}
          className="cursor-pointer object-cover"
          onClick={onOpen}
        />

        {/* Delete button */}
        {showDeleteButton && (
          <Button
            isIconOnly
            size="sm"
            color="danger"
            variant="flat"
            className="absolute top-2 right-2 z-10"
            onPress={handleDeleteClick}
            aria-label="Usuń zdjęcie"
          >
            <Trash2 size={16} />
          </Button>
        )}
      </div>

      {/* Confirmation modal */}
      <Modal 
        isOpen={isConfirmOpen} 
        onClose={onConfirmClose}
        placement="center"
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            Potwierdź usunięcie
          </ModalHeader>
          <ModalBody className='gap-2'>
            <span>Czy na pewno chcesz usunąć to zdjęcie?</span>            <div className="flex justify-center">
              <HeroUIImage
                src={convertedSrc}
                alt={alt}
                className="mt-2 mb-4 max-h-48 object-cover"
              />
            </div>
            <span className='text-danger'>Ta operacja jest nieodwracalna.</span>
          </ModalBody>
          <ModalFooter>
            <Button 
              color="default" 
              variant="flat"
              size='sm' 
              onPress={onConfirmClose}
              disabled={isDeleting}
            >
              Anuluj
            </Button>
            <Button 
              color="danger"
              size='sm'
              variant="flat" 
              onPress={handleConfirmDelete}
              isLoading={isDeleting}
            >
              Usuń
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Fullscreen modal */}
      <Modal 
        isOpen={isOpen} 
        onClose={onClose}
        size="full"
        hideCloseButton
        classNames={{
          backdrop: "bg-black/80",
          wrapper: "items-center justify-center",
          base: "bg-transparent shadow-none max-w-none max-h-none",
        }}
      >
        <ModalContent>
          <ModalBody className="p-0 flex items-center justify-center h-screen relative">
            <Button
              isIconOnly
              size="lg"
              color="default"
              variant="flat"
              className="absolute top-4 right-4 z-20 bg-white/10 backdrop-blur-sm"
              onPress={onClose}
              aria-label="Zamknij"
            >
              <X className="w-6 h-6 text-white" />
            </Button>
              <HeroUIImage
              src={convertedSrc}
              alt={alt}
              className="max-w-full max-h-full object-contain"
              removeWrapper
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Image;
